import Header from '../../components/Header';
import React, { FC, useContext, useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  useTheme,
} from '@mui/material';
import DailyControllingTable, {
  DCCampaignsTable,
  DCSoldLeadsTable,
} from '../../components/leads/DailyControllingTable';
import { FilterContext } from '../../contexts/FilterContext';
import { useSearchParams } from 'react-router-dom';

interface UnifiedDailyControllingPageProps {
  defaultLeadStatus: string;
  defaultStartDate: Date;
}

interface CustomerOption {
  value: string;
  label: string;
}

const customerOptions: CustomerOption[] = [
  { value: 'ft', label: 'Finanztrends' },
  { value: 'bg', label: 'Börse Global' },
  { value: 'sw', label: 'Stock-World' },
  { value: 'tt', label: 'Trading-Treff' },
  { value: 'be', label: 'Börse-Express' },
  { value: 'kme', label: 'Kapitalmarktexperten' },
  { value: 'foreign', label: 'Foreign Leads' },
  { value: 'non_finance', label: 'Non-Finance' },
];

const UnifiedDailyControllingPage: FC<UnifiedDailyControllingPageProps> = ({
  defaultLeadStatus,
  defaultStartDate,
}) => {
  const theme = useTheme();
  const { filterState, setFilterState } = useContext(FilterContext)!;
  const [searchParams, setSearchParams] = useSearchParams();

  // Get customer from URL params or default to 'ft'
  const [selectedCustomer, setSelectedCustomer] = useState(() => {
    return searchParams.get('customer') || 'ft';
  });

  useEffect(() => {
    setFilterState((prevState) => ({
      ...prevState,
      leadStatus: defaultLeadStatus,
      startDate: defaultStartDate,
    }));

    // Clean up on unmount - only reset leadStatus, don't contaminate dates
    return () => {
      setFilterState((prevState) => ({
        ...prevState,
        leadStatus: 'all',
      }));
    };
  }, [defaultLeadStatus, defaultStartDate, setFilterState]);

  const handleCustomerChange = (event: SelectChangeEvent<string>) => {
    const newCustomer = event.target.value;
    setSelectedCustomer(newCustomer);

    // Update URL parameter
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set('customer', newCustomer);
      return newParams;
    });
  };

  const selectedCustomerLabel =
    customerOptions.find((option) => option.value === selectedCustomer)?.label || 'Finanztrends';

  return (
    <Box m="20px">
      <Box justifyContent="space-between" alignItems="center" display="flex">
        <Header
          title={`Daily Controlling - ${selectedCustomerLabel}`}
          subtitle="Auswertungen zur Lead-Generierung und -Exports"
        />
        <Box sx={{ minWidth: 200 }}>
          <FormControl fullWidth>
            <InputLabel id="customer-select-label">Kunde</InputLabel>
            <Select
              labelId="customer-select-label"
              id="customer-select"
              value={selectedCustomer}
              label="Kunde"
              onChange={handleCustomerChange}
            >
              {customerOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box display="grid" gridTemplateColumns="1fr" gap="20px">
        <Box>
          {/* Live Kampagnen section only for Finanztrends */}
          {selectedCustomer === 'ft' && (
            <Box className="mt-2 !mb-2">
              <h2 className="!text-lg !font-bold">Live Kampagnen</h2>
              <DCCampaignsTable />
            </Box>
          )}

          <Box>
            <Box className="mt-2 !mb-2">
              <h2 className="!text-lg !font-bold">Verkaufte Leads</h2>
              <DCSoldLeadsTable customer={selectedCustomer} />
            </Box>
            <Box>
              <h2 className="!mt-8 !text-lg !font-bold">Alle Leads</h2>
              <DailyControllingTable customer={selectedCustomer} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UnifiedDailyControllingPage;
